import { useState } from "react";
import { CodeBlock, Badge, Table, Th, Td } from "../../components/ui";
import { SNIPPETS, API_BASE } from "../../lib/snippets";
import { cn } from "../../lib/cn";

const TOC = [
  ["quickstart", "Quickstart"],
  ["endpoints", "Endpoints"],
  ["plans", "Query plans"],
  ["errors", "Errors"],
  ["mcp", "MCP server"],
  ["integrations", "Integrations"],
  ["connector", "Connector"],
];

const RESPONSE = `{
  "data": {
    "columns": ["region", "avg_price"],
    "rows": [["Sydney", "1.989"]],
    "row_count": 1, "truncated": false, "next_cursor": null
  },
  "dbindex": { "plan": { "datasetId": "dst_…", "capability": "average_price", "filters": { "region": "Sydney", "fuel": "diesel" } },
               "plan_source": "capability_match", "routing": { "candidates": 3, "selected_score": 0.934 } },
  "provenance": {
    "dataset_id": "dst_…", "dataset_name": "NSW Fuel Prices", "provider": "Example Fuel Co", "provider_id": "org_…",
    "schema_version": 2, "contract_version": 4,
    "source_timestamp": "2026-10-16T05:12:04Z", "query_timestamp": "2026-10-16T05:12:22Z",
    "freshness_seconds": 18, "quality_score": 0.96,
    "transformations": ["station_id: hash"], "license": "dbindex-standard-query-1.0",
    "rights": { "liveQuery": true, "rag": false, "training": false }, "cache_status": "MISS"
  },
  "billing": { "query_price": "0.001200", "currency": "USD", "charged": true },
  "request_id": "req_…"
}`;

const PLAN = `POST /v1/query
{
  "plan": {
    "datasetId": "dst_…",
    "capability": "search_products",
    "filters": { "region": "Sydney", "price": { "lte": 500 } },
    "sort": [{ "field": "price", "dir": "asc" }],
    "limit": 25
  }
}`;

const ERR = `{
  "error": {
    "code": "price_exceeds_limit",
    "message": "Cheapest eligible route costs 0.002400 USD; max_query_price is 0.002000",
    "request_id": "req_…",
    "retryable": false
  }
}`;

export function Docs() {
  const [snip, setSnip] = useState(SNIPPETS[0]!.id);
  const current = SNIPPETS.find((s) => s.id === snip)!;
  return (
    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[200px_1fr]">
      <nav aria-label="Documentation" className="lg:sticky lg:top-20 lg:self-start">
        <ul className="space-y-1 text-sm">
          {TOC.map(([id, l]) => (
            <li key={id}>
              <a href={`#${id}`} className="block rounded px-2 py-1 text-fg-2 hover:bg-panel-2 hover:text-fg">
                {l}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <article className="min-w-0 space-y-14">
        <section id="quickstart">
          <h1 className="text-3xl font-semibold tracking-tight">DBIndex API</h1>
          <p className="mt-3 max-w-2xl text-fg-2">
            One vendor-neutral API for discovering and querying live data products. Create a consumer API key, ask a question or send a structured plan, and receive data with provenance, licence and price.
          </p>
          <ol className="mt-6 list-decimal space-y-2 pl-5 text-sm text-fg-2">
            <li>Create a consumer organisation and add credits under Billing.</li>
            <li>Create an API key with the <code className="font-mono text-fg">query</code> and <code className="font-mono text-fg">discover</code> scopes. The secret is shown once.</li>
            <li>Call <code className="font-mono text-fg">POST /v1/query</code>.</li>
          </ol>
          <CodeBlock className="mt-6" title="Request" code={SNIPPETS[0]!.code} />
          <CodeBlock className="mt-4" title="Response" code={RESPONSE} />
          <p className="mt-3 text-xs text-fg-3">
            Money is serialised as fixed 6-decimal strings and accounted internally in integer micro-units. Responses distinguish provider data (<code>data</code>), DBIndex-derived metadata (<code>dbindex</code>) and provenance.
          </p>
        </section>
        <section id="endpoints">
          <h2 className="text-xl font-semibold">Endpoints</h2>
          <p className="mt-2 text-sm text-fg-2">
            Base URL <code className="font-mono text-fg">{API_BASE}</code>. Authenticate with <code className="font-mono text-fg">Authorization: Bearer dbi_live_…</code>.
          </p>
          <Table className="mt-4 rounded-lg border border-border">
            <thead>
              <tr>
                <Th>Method</Th>
                <Th>Path</Th>
                <Th>Scope</Th>
                <Th>Purpose</Th>
              </tr>
            </thead>
            <tbody>
              {[
                ["POST", "/v1/discover", "discover", "Rank datasets able to answer a question"],
                ["POST", "/v1/plan", "query", "Resolve a question to a typed plan + price (no charge)"],
                ["POST", "/v1/query", "query", "Execute a question or plan; metered"],
                ["GET", "/v1/datasets/{id}", "metadata", "Signed dataset manifest"],
                ["GET", "/v1/datasets/{id}/schema", "metadata", "Serving entities and fields"],
                ["GET", "/v1/datasets/{id}/capabilities", "metadata", "Allowed operations, filters and limits"],
                ["POST", "/mcp", "discover, query", "Model Context Protocol (Streamable HTTP)"],
              ].map(([m, p, s, d]) => (
                <tr key={p}>
                  <Td>
                    <Badge tone={m === "GET" ? "info" : "accent"}>{m}</Badge>
                  </Td>
                  <Td mono>{p}</Td>
                  <Td mono className="text-fg-2">
                    {s}
                  </Td>
                  <Td className="text-fg-2">{d}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="mt-3 text-xs text-fg-3">
            Paid requests accept an <code>Idempotency-Key</code> header; retries with the same key are never charged twice.
          </p>
        </section>
        <section id="plans">
          <h2 className="text-xl font-semibold">Query plans</h2>
          <p className="mt-2 max-w-2xl text-sm text-fg-2">
            Natural language resolves to a typed QueryPlan validated against the dataset's approved capabilities. Unknown capabilities, fields or operators are rejected — DBIndex never guesses and never generates SQL from model output.
          </p>
          <CodeBlock className="mt-4" title="Structured invocation" code={PLAN} />
        </section>
        <section id="errors">
          <h2 className="text-xl font-semibold">Errors</h2>
          <CodeBlock className="mt-4" title="Error envelope" code={ERR} />
          <p className="mt-3 text-sm text-fg-2">
            Common codes: <code>unauthenticated</code>, <code>forbidden</code>, <code>invalid_plan</code>, <code>unsupported_capability</code>, <code>no_route</code>, <code>price_exceeds_limit</code>, <code>insufficient_funds</code>, <code>budget_exceeded</code>, <code>rate_limited</code>, <code>dataset_stale</code>.
          </p>
        </section>
        <section id="mcp">
          <h2 className="text-xl font-semibold">MCP server</h2>
          <p className="mt-2 max-w-2xl text-sm text-fg-2">
            Tools: <code>dbindex.discover</code>, <code>dbindex.describe</code>, <code>dbindex.query</code>, <code>dbindex.compare_sources</code>, <code>dbindex.estimate_cost</code>. The server enforces the API key's scopes, budgets and per-query price cap.
          </p>
          <CodeBlock className="mt-4" title="mcp.json" code={SNIPPETS.find((s) => s.id === "mcp")!.code} />
        </section>
        <section id="integrations">
          <h2 className="text-xl font-semibold">Integrations</h2>
          <p className="mt-2 text-sm text-fg-2">All integrations call the same universal API. No model-specific backend exists.</p>
          <div className="mt-4 flex flex-wrap gap-1" role="tablist" aria-label="Integration">
            {SNIPPETS.map((s) => (
              <button
                key={s.id}
                role="tab"
                aria-selected={s.id === snip}
                onClick={() => setSnip(s.id)}
                className={cn("rounded-md border px-2.5 py-1 text-xs", s.id === snip ? "border-accent bg-accent-soft text-fg" : "border-border text-fg-2 hover:text-fg")}
              >
                {s.label}
              </button>
            ))}
          </div>
          <CodeBlock className="mt-3" title={`${current.label} · ${current.language}`} code={current.code} />
        </section>
        <section id="connector">
          <h2 className="text-xl font-semibold">Connector</h2>
          <p className="mt-2 max-w-2xl text-sm text-fg-2">
            <code>dbindex-connector</code> is a small open-source service that runs inside your network with a read-only database account. It makes outbound-only TLS connections, verifies signed exposure contracts, applies transformations locally and never executes arbitrary commands or SQL.
          </p>
          <CodeBlock
            className="mt-4"
            title="docker"
            code={`docker run -d --name dbindex-connector --restart unless-stopped \\
  -e DBINDEX_ENROLLMENT_TOKEN=<one-time token from the dashboard> \\
  -e DBINDEX_SOURCE_DSN="postgres://dbindex_readonly@db.internal:5432/app?sslmode=require" \\
  -v dbindex-connector:/var/lib/dbindex \\
  ghcr.io/dbindex/connector:latest`}
          />
        </section>
      </article>
    </div>
  );
}
